import React from 'react';
import DecisionTreeEngine from './DecisionTreeEngine';

/**
 * LocationTree - "Where Should I Live?" Decision Tree
 * 
 * A branching decision tree that splits into two paths:
 * - Private Builder path (Gates A, B)
 * - Visible Mission Builder path (Gates 04, 05, 06)
 * 
 * The branching logic lives here in getNextGates, not in the engine.
 */

// Tree configuration
const treeConfig = {
  title: "Should I Live in SF or LA?",
  subtitle: "BINARY GAME DECISION TREE // FEBRUARY 2026",
  domain: "Mission / Identity / Location",
  identityQuestion: "What kind of place do I live?"
};

// Main gates (always shown first)
const mainGates = [
  {
    id: "01",
    type: "YES/NO",
    question: "Am I regulated enough to decide this right now?",
    noText: "If reactive, this tree is not valid right now. Regulate first. Come back in 24 hours. Decisions made from a panicked or triggered state will not hold up under pressure. That is not weakness. That is how nervous systems work."
  },
  {
    id: "02",
    type: "YES/NO",
    badge: "DOMINO",
    question: "Can I name in 30 seconds whether I'm a Visible Mission Builder or a Private Builder right now?",
    noText: "You are not clear enough yet. That is fine. Default to the Private Builder path for now. That is not failure. It is just where you are today. When clarity comes, re-run the tree.",
    note: "This is the domino gate. Everything else falls from this one.",
    footnote: "This is not moral. This is seasonal.",
    continuesBoth: true // NO defaults to Private Builder path, doesn't exit
  },
  {
    id: "03",
    type: "THIS/THAT",
    question: "Which one? Private Builder or Visible Mission Builder?",
    yesLabel: "VISIBLE MISSION BUILDER",
    noLabel: "PRIVATE BUILDER",
    noText: "You are in an incubation season. You are building quietly, recovering, getting your foundation solid. Location is a lifestyle choice, not a mission choice.",
    yesText: "You are ready to do the work at full volume. Location needs to serve the mission.",
    continuesBoth: true // Both paths continue to their respective gates
  }
];

// Private Builder path gates
const privateBuilderGates = [
  {
    id: "A",
    type: "YES/NO",
    question: "Does this city support who I want to become personally?",
    subtext: "Artistic, cultural, intellectual, relational. Does the city feed the version of you that is emerging? Not the version you were. The version you are becoming.",
    noText: "Not your city.",
    pathLabel: "PRIVATE BUILDER PATH"
  },
  {
    id: "B",
    type: "YES/NO",
    question: "Can I live here 3-5 years without nervous system erosion?",
    subtext: "Not can you survive there. Can you sustain there. Three to five years without the city itself grinding you down. Cost of living, pace, isolation, climate, commute, noise. All of it.",
    noText: "Not your city."
  }
];

// Visible Mission Builder path gates
const visibleBuilderGates = [
  {
    id: "04",
    type: "YES/NO",
    badge: "CONDITIONAL",
    question: "Are there credible, specific, evidence-based threats that require a safety adjustment? Not vibes. Not suspicion. Evidence.",
    noText: "The fear is real but the threat is not confirmed. Do not let an unverified story pick your zip code. Continue.",
    yesText: "Build a safety plan. Privacy practices, exposure management, daily routines, trusted allies, housing discretion. The plan is not about which city. It travels with you. A real threat does not disappear because you change your zip code. You manage it with structure, not geography.",
    continuesBoth: true, // Both YES and NO continue to next gate
    pathLabel: "VISIBLE MISSION BUILDER PATH"
  },
  {
    id: "05",
    type: "YES/NO",
    question: "Does this city materially increase proximity to my mission?",
    subtext: "Network, access, iteration speed, influence. Does being physically present in this city put you closer to the people, the conversations, and the leverage points that move your mission forward?",
    noText: "Not your city. Try another."
  },
  {
    id: "06",
    type: "SOMATIC",
    question: "When I imagine being in this city doing the work at full volume with no cap, does my body feel expansion or contraction?",
    subtext: "Not what your head thinks. What your body feels. Close your eyes. Picture yourself there. Working. Building. Fully visible. No ceiling. What happens in your chest, your shoulders, your gut?",
    yesLabel: "EXPANSION",
    noLabel: "CONTRACTION",
    noText: "Something else is unresolved. That is not a location problem. Look at it separately."
  }
];

// Results by path
const results = {
  privateBuilder: "This is your city. You are choosing based on personal growth and sustainability, not mission optimization. That is valid for this season.",
  visibleBuilder: "You already know. This is your mission base."
};

/**
 * getNextGates - Returns the flat sequence of gates based on current state
 * 
 * The branching logic:
 * - Gates 01, 02, 03 are always first
 * - After Gate 02 with NO: default to Private Builder path
 * - After Gate 03: branch based on choice (YES = Visible, NO = Private)
 */
const getNextGates = (currentGateIndex, answers) => {
  // Start with main gates
  let sequence = [...mainGates];
  
  // Check if we need to determine the branch
  const gate02Answer = answers['02'];
  const gate03Answer = answers['03'];
  
  // If Gate 02 was answered NO, default to Private Builder
  if (gate02Answer === 'no') {
    sequence = [...mainGates.slice(0, 2), ...privateBuilderGates];
    return sequence;
  }
  
  // If Gate 03 has been answered, add the appropriate branch
  if (gate03Answer !== undefined) {
    if (gate03Answer === 'yes') {
      // Visible Mission Builder path
      sequence = [...mainGates, ...visibleBuilderGates];
    } else {
      // Private Builder path
      sequence = [...mainGates, ...privateBuilderGates];
    }
  }
  
  return sequence;
};

/**
 * getResult - Returns the final result based on the path taken
 */
const getResult = (answers) => {
  const gate02Answer = answers['02'];
  const gate03Answer = answers['03'];
  
  // Determine which path was taken
  let pathType = 'privateBuilder';
  
  if (gate02Answer === 'no') {
    pathType = 'privateBuilder';
  } else if (gate03Answer === 'yes') {
    pathType = 'visibleBuilder';
  }
  
  return {
    text: results[pathType],
    type: pathType
  };
};

const LocationTree = ({ onClose }) => {
  return (
    <DecisionTreeEngine
      treeConfig={treeConfig}
      getNextGates={getNextGates}
      getResult={getResult}
      onClose={onClose}
    />
  );
};

export default LocationTree;
